import { type CSSObject, Box, Table, TableContainer, Tbody, Td, Text, Th, Thead, Tr } from "@chakra-ui/react";
import React from "react";
import { CheckIcon } from "ui/icons";
import { type TableProps } from "./data";
import { TABLE_HEAD_WIDTH, TABLE_CELL_WIDTH } from "./utils";

const tableHeadTitleStyles: CSSObject = {
  color: "text.primary",
  fontSize: "sm",
  lineHeight: 1.5,
  m: 0,
  p: 4,
};

const planHeadingStyles: CSSObject = {
  borderLeft: "1px solid",
  borderLeftColor: "base.border",
  w: TABLE_CELL_WIDTH,
  maxW: TABLE_CELL_WIDTH,
};

const hiddenCellStyles: CSSObject = {
  opacity: 0, // Keep display for screen readers
};

const hiddenRowStyles: CSSObject = {
  visibility: "collapse",
};

// This table is built out for accessibility best practices.
// Some elements like aria-labels and table headings may not visibly
// appear on the screen, but will help clarify meaning for screen readers.

export function PricingTable({
  groupTitle,
  title,
  tableBody,
  isFirst,
  extendedBorderHeight,
}: TableProps & { groupTitle: string; title?: string; isFirst: boolean; extendedBorderHeight: number }) {
  const extendedBorderStyles: CSSObject = {
    pos: "relative",
    _before: {
      content: "''",
      pos: "absolute",
      left: "-1px", // Table cell border alignment
      right: 0,
      bottom: "100%",
      height: `${extendedBorderHeight}px`,
      borderLeft: "1px solid",
      borderColor: "base.border",
    },
  };

  const tableHeadPlanStyles = {
    ...tableHeadTitleStyles,
    ...planHeadingStyles,
    ...(isFirst ? extendedBorderStyles : {}),
  };

  const tableCaption = `Compare plans by ${groupTitle + (title ? `: ${title}` : "")}`;

  return (
    <TableContainer overflowX="visible" overflowY="visible">
      <Table variant="unstyled">
        <Box
          as="caption"
          opacity={0} // Keep visible for screen readers
          height={0}
        >
          {tableCaption}
        </Box>
        <Thead>
          <Tr sx={title ? undefined : { ...hiddenRowStyles }}>
            <Th sx={{ ...tableHeadTitleStyles }} maxW={TABLE_HEAD_WIDTH}>
              {title ?? groupTitle}
            </Th>
            <Th sx={tableHeadPlanStyles}>
              <Text as="span" sx={{ ...hiddenCellStyles }}>
                $0 free forever
              </Text>
            </Th>
            <Th sx={tableHeadPlanStyles}>
              <Text as="span" sx={{ ...hiddenCellStyles }}>
                $350/month
              </Text>
            </Th>
            <Th sx={tableHeadPlanStyles}>
              <Text as="span" sx={{ ...hiddenCellStyles }}>
                Custom
              </Text>
            </Th>
          </Tr>
        </Thead>
        <Tbody>
          {tableBody?.map((row, rowIndex) => (
            <Tr
              key={row.rowHead}
              pos="relative"
              transition="all .15s ease-in-out"
              _hover={{
                bg: "base.background",
              }}
            >
              <Th
                color="text.primary"
                borderLeftRadius="md"
                fontSize="sm"
                fontWeight="medium"
                lineHeight={1.5}
                px={4}
                textTransform="none"
                whiteSpace="normal"
                maxW={TABLE_HEAD_WIDTH}
              >
                {row.rowHead}
              </Th>
              {row.rowData.map((data, dataIndex) => (
                <Td
                  key={`${row.rowHead}-${dataIndex}`}
                  border="none"
                  borderLeft="1px solid"
                  borderLeftColor="base.border"
                  borderRightRadius={dataIndex === row.rowData.length - 1 ? "md" : "none"}
                  fontSize="sm"
                  fontWeight="semibold"
                  justifyContent="center"
                  opacity={data === "N/A" ? 0 : 1} // Keep display visible for screen readers.
                  textAlign="center"
                  w={TABLE_CELL_WIDTH}
                  maxW={TABLE_CELL_WIDTH}
                  sx={rowIndex === 0 && !title ? { ...extendedBorderStyles } : undefined}
                >
                  {data === "check" && (
                    <Text textAlign="center">
                      <CheckIcon color="text.primary" boxSize={5} />
                    </Text>
                  )}
                  {data !== "check" && data}
                </Td>
              ))}
            </Tr>
          ))}
        </Tbody>
      </Table>
    </TableContainer>
  );
}
